import { Trophy, RotateCcw, Copy, FileText, FileJson } from 'lucide-react';
import type { CostOfDelayItem } from '../types';

interface ResultsViewProps {
  items: CostOfDelayItem[];
  onReset: () => void;
}

export function ResultsView({ items, onReset }: ResultsViewProps) {
  const sortedItems = [...items].sort((a, b) => {
    if (a.costOfDelay === null) return 1;
    if (b.costOfDelay === null) return -1;
    return b.costOfDelay - a.costOfDelay;
  });

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# Cost of Delay Results\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        content += `Cost of Delay (CoD) = Value Lost Due to Delay + Cost to Restart or Rework\n\n`;
        content += `| Rank | Item | Value Lost | Cost to Restart | Cost of Delay |\n`;
        content += `|------|------|------------|-----------------|---------------|\n`;
        sortedItems.forEach((item, index) => {
          content += `| ${index + 1} | ${item.name} | ${item.valueLost ?? 'N/A'} | ${item.costToRestart ?? 'N/A'} | ${item.costOfDelay !== null ? item.costOfDelay.toFixed(2) : 'N/A'} |\n`;
        });
        filename = `cost-of-delay-results-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = JSON.stringify(sortedItems, null, 2);
        filename = `cost-of-delay-results-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = sortedItems.map((item, index) => 
          `${index + 1}. ${item.name} - CoD: ${item.costOfDelay !== null ? item.costOfDelay.toFixed(2) : 'N/A'} (Value Lost: ${item.valueLost ?? 'N/A'}, Cost to Restart: ${item.costToRestart ?? 'N/A'})`
        ).join('\n');
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Cost of Delay Prioritization Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Items ranked by Cost of Delay: <strong>Value Lost + Cost to Restart</strong>. Higher scores indicate higher priority (items that get more "expensive" over time).
        </p>

        {/* Results Table */}
        <div className="mb-6 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--surface)] border-b-2 border-[var(--rule)]">
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Rank</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-[var(--text-sec)]">Item</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Value Lost</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Cost to Restart</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-[var(--text-sec)]">Cost of Delay</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item, index) => {
                const rank = index + 1;
                const codValue = item.costOfDelay !== null ? item.costOfDelay.toFixed(2) : 'N/A';
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-[var(--rule)] hover:bg-[var(--surface)] ${
                      rank === 1 ? 'bg-[var(--mustard-dim)]' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-[var(--text-pri)]">
                      {rank === 1 ? <Trophy className="h-5 w-5 text-[var(--mustard)] inline" /> : rank}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)]">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-center text-[var(--text-sec)]">{item.valueLost ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-center text-[var(--text-sec)]">{item.costToRestart ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-center font-bold text-[var(--text-ter)]">{codValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Interpretation */}
        <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <h3 className="font-semibold text-[var(--text-sec)] mb-2">Interpretation</h3>
          <p className="text-sm text-[var(--text-sec)] mb-2">
            <strong>Cost of Delay introduces time value to prioritization.</strong> The top-ranked item ({sortedItems[0]?.name}) has the highest combined cost of value lost and restart/rework expenses.
          </p>
          <p className="text-sm text-[var(--text-sec)]">
            Items with higher Cost of Delay become more "expensive" over time. Prioritize these to avoid paying opportunity costs and missing out on potential value. Consider that a quicker win for a smaller item may be more valuable than waiting for a larger, longer-term item.
          </p>
        </div>

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


